import {
	ActionListCheckbox,
	ActionListDivider,
	ActionListItem,
	Toolbar
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { ReactElement } from "react";
import { TextAndToolbar } from "./TextAndToolbar";
import { DatabaseOrTempObjectId } from "client_legacy/ndaw/tempOrDatabaseId";
import { Greeting } from "./Greeting";
import styles from "./Header.module.less";
import { useValueEqualMemo } from "client/Hooks";
import { TileGroupType } from "client/Api/Workspace/workspace";

export type SpaceTileGroupMenuOptions = {
	canCreateNewPersonalGroup: boolean;
	canCreateNewSharedGroup: boolean;
	canChangeDefaultSharedGroupsOrder: boolean;
	tileGroups: readonly TileGroupMenuItem[];
};

export type TileGroupMenuItem = {
	id: DatabaseOrTempObjectId;
	name: string;
	type: TileGroupType;
	isVisible: boolean;
};

export type HeaderProps = {
	isInEditMode: boolean;
	canEdit: boolean;
	tileGroupMenuOptions: SpaceTileGroupMenuOptions;
	shouldRenderBackdrop: boolean;
	onAddTiles(): void;
	onEnterEditMode(): void;
	onSave(): void;
	onCancel(): void;
	onCreateNewPersonalGroup(): void;
	onCreateNewSharedGroup(): void;
	onChangeShareGroupsOrder(): void;
	onChangeTileGroupVisibility(id: DatabaseOrTempObjectId, isVisible: boolean): void;
};

export const Header = (props: HeaderProps): ReactElement => {
	const {
		isInEditMode,
		canEdit,
		shouldRenderBackdrop,
		onEnterEditMode,
		onSave,
		onCancel,
		onAddTiles,
		onCreateNewPersonalGroup,
		onCreateNewSharedGroup,
		onChangeShareGroupsOrder,
		onChangeTileGroupVisibility,
		tileGroupMenuOptions
	} = props;

	const renderTitle = (): ReactElement | string => {
		if (isInEditMode) {
			return (
				<span className={styles.editModeTitle}>{localize("_EditMode")}</span>
			);
		}

		return (
			<Greeting />
		);
	};

	const canManageGroups = useValueEqualMemo(
		(): boolean => {
			const {
				canChangeDefaultSharedGroupsOrder,
				canCreateNewPersonalGroup,
				canCreateNewSharedGroup,
				tileGroups
			} = tileGroupMenuOptions;

			const hasManageSection: boolean = canCreateNewPersonalGroup
				|| canCreateNewSharedGroup
				|| canChangeDefaultSharedGroupsOrder;

			return hasManageSection || tileGroups.length > 0;
		},
		[tileGroupMenuOptions]
	);

	const renderToolbar = (): ReactElement | null => {
		if (isInEditMode) {
			return (
				<>
					<Toolbar.Button
						kind="primary"
						icon="floppy-disk"
						text={localize("_Save")}
						onClick={onSave}
						trackingId="Save"
					/>
					<Toolbar.Button
						kind="secondary"
						icon="xmark"
						text={localize("_Cancel")}
						onClick={onCancel}
						trackingId="Cancel"
					/>
					<Toolbar.Button
						kind="secondary"
						icon="plus"
						text={localize("_AddNewTiles")}
						onClick={onAddTiles}
						trackingId="NewApp"
					/>
					{canManageGroups && (
						<TileGroupMenuButton
							tileGroupMenuOptions={tileGroupMenuOptions}
							onCreateNewPersonalGroup={onCreateNewPersonalGroup}
							onCreateNewSharedGroup={onCreateNewSharedGroup}
							onChangeShareGroupsOrder={onChangeShareGroupsOrder}
							onChangeTileGroupVisibility={onChangeTileGroupVisibility}
						/>
					)}
				</>
			);
		} else if (canEdit) {
			return (
				<Toolbar.Button
					kind="secondary"
					icon="pen"
					text={localize("_Edit")}
					onClick={onEnterEditMode}
					trackingId="EditWorkspace"
				/>
			);
		} else {
			return null;
		}
	};

	return (
		<TextAndToolbar
			shouldRenderBackdrop={shouldRenderBackdrop}
			text={renderTitle()}
			toolbarItems={renderToolbar()}
		/>
	);
};
Header.displayName = "Header";

type TileGroupMenuButtonProps = {
	tileGroupMenuOptions: SpaceTileGroupMenuOptions;
	onCreateNewPersonalGroup(): void;
	onCreateNewSharedGroup(): void;
	onChangeShareGroupsOrder(): void;
	onChangeTileGroupVisibility(id: DatabaseOrTempObjectId, isVisible: boolean): void;
};

const TileGroupMenuButton = (props: TileGroupMenuButtonProps): ReactElement | null => {
	const {
		tileGroupMenuOptions,
		onCreateNewPersonalGroup,
		onCreateNewSharedGroup,
		onChangeShareGroupsOrder,
		onChangeTileGroupVisibility
	} = props;

	const {
		canChangeDefaultSharedGroupsOrder,
		canCreateNewPersonalGroup,
		canCreateNewSharedGroup,
		tileGroups
	} = tileGroupMenuOptions;

	return (
		<Toolbar.MenuButton
			kind="secondary"
			text={localize("_Groups")}
			icon="user-group"
			rightIcon="chevron-down"
			trackingId="Groups"
		>
			{canCreateNewPersonalGroup && (
				<ActionListItem
					icon="plus"
					text={localize("_NewPersonalGroup")}
					onClick={onCreateNewPersonalGroup}
					trackingId="NewPersonalGroup"
				/>
			)}
			{canCreateNewSharedGroup && (
				<ActionListItem
					icon="plus"
					text={localize("_NewSharedGroup")}
					onClick={onCreateNewSharedGroup}
					trackingId="AddSharedGroup"
				/>
			)}
			{canChangeDefaultSharedGroupsOrder && (
				<ActionListItem
					icon="grip-lines"
					text={localize("_OrderSharedGroups")}
					onClick={onChangeShareGroupsOrder}
					trackingId="OrderSharedGroups"
				/>
			)}
			{tileGroups.length > 0 && (
				<ActionListDivider />
			)}
			{tileGroups.map(
				tg => (
					<ActionListCheckbox
						key={tg.id.toString()}
						text={tg.name}
						value={tg.isVisible}
						onChange={(isVisible) => onChangeTileGroupVisibility(tg.id, isVisible)}
						shouldNotAutoCloseContainingDropOver
						icon={
							tg.type === TileGroupType.Shared
								? "user-group"
								: undefined
						}
						trackingId={"ToggleGroupVisibility_" + tg.id.toString()}
					/>
				)
			)}
		</Toolbar.MenuButton>
	);
};
TileGroupMenuButton.displayName = "TileGroupMenuButton";